import React from 'react'
import {
  PiArrowRight,
  PiTrash,
  PiPencilSimple,
  PiListBullets,
} from 'react-icons/pi'
import { cn } from '../UI/cn'

interface ActionIconProps {
  style?: React.CSSProperties
  onClick: () => void
  disabled?: boolean
  testId?: string
  icon?: React.ComponentType<{
    size: number
    color: string
    style: React.CSSProperties
  }>
}

// Icons inherit `currentColor`, so color is driven by the wrapper button's
// CSS color: Tailwind gray-500 idle, gray-900 on hover, gray-300 disabled.
const ICON_SIZE = 20
const iconButtonClass =
  'inline-flex items-center justify-center bg-transparent border-none p-0 text-[#6B7280] hover:text-neutral-900 disabled:text-[#D0D5DD]'

export const SeeIcon = ({
  onClick,
  style,
  icon: Icon = PiArrowRight,
}: ActionIconProps) => {
  return (
    <button
      type="button"
      className={iconButtonClass}
      style={{ cursor: 'pointer' }}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      <Icon size={ICON_SIZE} color="currentColor" style={style ?? {}} />
    </button>
  )
}

export const DeleteIcon = ({
  onClick,
  style,
  disabled,
  testId,
  icon: Icon = PiTrash,
}: ActionIconProps) => {
  return (
    <button
      type="button"
      data-testid={testId}
      className={iconButtonClass}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      onClick={(e) => {
        e.stopPropagation()
        if (!disabled) {
          onClick()
        }
      }}
      disabled={disabled}
    >
      <Icon size={ICON_SIZE} color="currentColor" style={style ?? {}} />
    </button>
  )
}

export const EditIcon = ({
  onClick,
  style,
  disabled,
  icon: Icon = PiPencilSimple,
}: ActionIconProps) => {
  return (
    <button
      type="button"
      className={iconButtonClass}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      onClick={(e) => {
        e.stopPropagation()
        if (!disabled) {
          onClick()
        }
      }}
      disabled={disabled}
    >
      <Icon
        size={ICON_SIZE}
        color="currentColor"
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', ...style }}
      />
    </button>
  )
}

export const ViewIcon = ({
  onClick,
  style,
  disabled,
  icon: Icon = PiListBullets,
}: ActionIconProps) => {
  return (
    <button
      type="button"
      className={iconButtonClass}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      onClick={(e) => {
        e.stopPropagation()
        if (!disabled) {
          onClick()
        }
      }}
      disabled={disabled}
    >
      <Icon
        size={ICON_SIZE}
        color="currentColor"
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', ...style }}
      />
    </button>
  )
}

type ActionButtonsProps = {
  onEdit?: () => void
  onDelete?: () => void
  onSee?: () => void
  className?: string
  editDisabled?: boolean
  deleteDisabled?: boolean
  deleteTestId?: string
  editIcon?: React.ComponentType<{
    size: number
    color: string
    style: React.CSSProperties
  }>
  deleteIcon?: React.ComponentType<{
    size: number
    color: string
    style: React.CSSProperties
  }>
  seeIcon?: React.ComponentType<{
    size: number
    color: string
    style: React.CSSProperties
  }>
  prepend?: React.ReactNode
  append?: React.ReactNode
}

export function ActionButtons(props: ActionButtonsProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-end pr-5 gap-2 flex-1',
        props.className
      )}
    >
      {props.prepend}
      {props.onEdit && (
        <EditIcon
          onClick={props.onEdit}
          icon={props.editIcon}
          disabled={props.editDisabled}
        />
      )}
      {props.onDelete && (
        <DeleteIcon
          onClick={props.onDelete}
          icon={props.deleteIcon}
          disabled={props.deleteDisabled}
          testId={props.deleteTestId}
        />
      )}
      {props.onSee && <SeeIcon onClick={props.onSee} icon={props.seeIcon} />}
      {props.append}
    </div>
  )
}

type JobActionButonProps = {
  onView: () => void
  className?: string
  icon?: React.ComponentType<{
    size: number
    color: string
    style: React.CSSProperties
  }>
}

export function JobActionButtons(props: JobActionButonProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-center pr-5 gap-2 flex-1',
        props.className
      )}
    >
      <ViewIcon onClick={props.onView} icon={props.icon} />
    </div>
  )
}
